import { useAuthorizationStore } from '../stores/authorization';
import { storeToRefs } from 'pinia';
import type {
    Role,
    Permission,
} from '@precon-tools-suite-by-shared/js-precon-rbac';
import {
    AuthorizationError,
    type Ability,
    type AsyncAbility,
    type AuthorizationFailedResult,
    type AuthorizationResult,
    type RoleOrPermission,
} from '@precon-tools-suite-by-shared/shared-auth-js-ability-checks';
import { until } from '@vueuse/core';
// import { useUnauthorizedToast } from '@precon-tools-suite-by-shared/shared-common-toasts-ui';

export function useAuthorization() {
    const store = useAuthorizationStore();

    const {
        teamId,
        userId,
        isSuperUser,
        authContext,
        loading,
        roles,
        permissions,
    } = storeToRefs(store);

    async function waitTillLoaded(): Promise<void> {
        await until(loading).not.toBeTruthy();
    }

    async function hasRole(role: Role): Promise<boolean> {
        await waitTillLoaded();
        return store.authContext.hasRole(role);
    }

    async function hasRoles(...roles: Role[]): Promise<boolean> {
        await waitTillLoaded();
        return store.authContext.hasRoles(...roles);
    }

    async function hasAnyRole(...roles: Role[]): Promise<boolean> {
        await waitTillLoaded();
        return store.authContext.hasAnyRole(...roles);
    }

    async function hasPermission(permission: Permission): Promise<boolean> {
        await waitTillLoaded();
        return store.authContext.hasPermission(permission);
    }

    async function hasPermissions(
        ...permissions: Permission[]
    ): Promise<boolean> {
        await waitTillLoaded();
        return store.authContext.hasPermissions(...permissions);
    }

    async function hasAnyPermission(
        ...permissions: Permission[]
    ): Promise<boolean> {
        await waitTillLoaded();
        return store.authContext.hasAnyPermission(...permissions);
    }

    async function has(
        ...rolesPlusPermissions: RoleOrPermission[]
    ): Promise<boolean> {
        await waitTillLoaded();
        return store.authContext.has(...rolesPlusPermissions);
    }

    async function hasAny(
        ...rolesPlusPermissions: RoleOrPermission[]
    ): Promise<boolean> {
        await waitTillLoaded();
        return store.authContext.hasAny(...rolesPlusPermissions);
    }

    async function check<TParams extends any[]>(
        ability: Ability<TParams> | AsyncAbility<TParams>,
        ...parameters: TParams
    ): Promise<boolean> {
        await waitTillLoaded();
        return store.authContext.check(
            ability as AsyncAbility<TParams>,
            ...parameters
        );
    }

    async function inspect<TParams extends any[]>(
        ability: Ability<TParams> | AsyncAbility<TParams>,
        ...parameters: TParams
    ): Promise<AuthorizationResult> {
        await waitTillLoaded();
        return store.authContext.inspect(
            ability as AsyncAbility<TParams>,
            ...parameters
        );
    }

    // const toast = useUnauthorizedToast();

    async function authorize<TParams extends any[]>(
        ability: Ability<TParams> | AsyncAbility<TParams>,
        ...parameters: TParams
    ): Promise<void> {
        const authResult = await inspect(ability, ...parameters);

        if (authResult.allowed) return;

        console.error(`UNAUTHORIZED ABILITY '${ability}'.`, {
            parameters,
            ...authResult,
            authContext: authContext.value,
        });

        // toast.add(authResult as AuthorizationFailedResult);
        throw new AuthorizationError(authResult as AuthorizationFailedResult);
    }

    return {
        authContext,
        loading,
        teamId,
        userId,
        hasRole,
        hasRoles,
        hasAnyRole,
        hasPermission,
        hasPermissions,
        hasAnyPermission,
        has,
        hasAny,
        check,
        inspect,
        isSuperUser,
        authorize,
        roles,
        permissions,
    };
}
